import { useEffect, useState } from 'react';
import { api } from '../lib/api.ts';
import { boot } from './store.ts';

/**
 * The sign-in screen. Deliberately bare: two boxes, nothing naming the
 * product or explaining itself, and no button — Enter submits. It pins itself
 * to dark, because preferences only load once you are through it.
 */
export function AuthView() {
  const [needsSetup, setNeedsSetup] = useState<boolean | null>(null);

  useEffect(() => {
    document.documentElement.dataset.theme = 'dark';
    api.get<{ needs_setup: boolean }>('/api/setup-state')
      .then((s) => setNeedsSetup(s.needs_setup))
      .catch(() => setNeedsSetup(false));
  }, []);

  if (needsSetup === null) return <div className="auth" />;
  return needsSetup ? <SetupForm /> : <LoginForm />;
}

function useSubmit(run: () => Promise<void>) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try { await run(); } catch (err: any) {
      setError(err?.message ?? 'Something went wrong.');
    } finally { setBusy(false); }
  };
  return { busy, error, submit };
}

/**
 * No visible label by design. `aria-label` still carries the name, so screen
 * readers and password managers know what each box is without anything being
 * drawn on screen.
 */
function Field({
  label, value, onChange, type, autoFocus, placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  autoFocus?: boolean;
  placeholder?: string;
}) {
  return (
    <input
      className="auth-field"
      aria-label={label}
      placeholder={placeholder}
      type={type ?? 'text'}
      value={value}
      required
      autoFocus={autoFocus}
      autoComplete={type === 'password' ? 'current-password' : 'username'}
      spellCheck={false}
      autoCapitalize="off"
      autoCorrect="off"
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function LoginForm() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const { busy, error, submit } = useSubmit(async () => {
    await api.post('/api/auth/login', { username, password });
    await boot();
  });

  return (
    <div className="auth">
      <form className={`auth-form${busy ? ' busy' : ''}`} onSubmit={submit} noValidate>
        {error ? <div className="auth-error" role="alert">{error}</div> : null}
        <Field label="ID" value={username} onChange={setUsername} autoFocus />
        <Field label="Password" value={password} onChange={setPassword} type="password" />
        {/* No button: a single-submit form sends on Enter from either field.
            Kept for screen readers and for browsers that need a submitter. */}
        <button type="submit" className="sr" tabIndex={-1} aria-label="Sign in" />
      </form>
    </div>
  );
}

/** Only reachable on a database with no account and no VNOTE_USER set. */
function SetupForm() {
  const [workspace, setWorkspace] = useState('V.note');
  const [realName, setRealName] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const { error, submit } = useSubmit(async () => {
    await api.post('/api/auth/setup', {
      workspace_name: workspace,
      real_name: realName,
      username,
      password,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
    await boot();
  });

  return (
    <div className="auth">
      <form className="auth-form" onSubmit={submit} noValidate>
        {error ? <div className="auth-error" role="alert">{error}</div> : null}
        <Field label="Workspace" value={workspace} onChange={setWorkspace} autoFocus placeholder="Workspace" />
        <Field label="Name" value={realName} onChange={setRealName} placeholder="Name" />
        <Field label="ID" value={username} onChange={setUsername} placeholder="ID" />
        <Field label="Password" value={password} onChange={setPassword} type="password" placeholder="Password" />
        <button type="submit" className="sr" tabIndex={-1} aria-label="Create" />
      </form>
    </div>
  );
}
